/**
 * Lax parser for chat HTML-like inline directives (`<badge>`, `<cite>`).
 *
 * The chat surface used to ship a soup of bracket-and-colon notations
 * (`[badge:tone]label[/badge]`, `[[cite:id]]`, `[[fu:label|prompt]]`,
 * `:::callout{…}`). Models had no pretraining for any of them and
 * regularly emitted near-misses (`[badge:type=positive]`, `:::callout(…)`,
 * `[badge:tone="positive"]label[/badge]`) that surfaced raw in the chat
 * UI. PLT-4590 replaces the inline ones with HTML-like tags — `<badge>` and
 * `<cite>` — which models *have* seen orders of magnitude more of in
 * pretraining (well-formed XML, JSX, MDX, etc.).
 *
 * Lax parser rules — agreed with Janne in the PLT-4590 thread:
 *
 *   - Tag and attribute names are matched case-insensitively. `<BADGE
 *     TONE="positive">` parses the same as `<badge tone="positive">`.
 *   - Attribute values accept double-quoted, single-quoted, smart-quoted
 *     (curly typographic quotes), and unquoted forms. Whitespace around
 *     `=` is tolerated.
 *   - Unknown attributes are silently dropped — only the named-and-typed
 *     attribute set the consumer asks for is returned. (E.g. the badge
 *     consumer asks for `tone`; an LLM-emitted `<badge variant="warning"
 *     tone="positive">` keeps the tone and drops `variant`.)
 *   - Missing required attributes are reported via `defaults` — the
 *     consumer chooses how to handle them (e.g. badge defaults to
 *     `tone="neutral"`).
 *   - Malformed or unclosed tags don't match. The remark plugin walks
 *     past them and the post-render sanitizer (PLT-4590 part of the
 *     telemetry layer) flags any leftover `<badge` / `<cite` text that
 *     reached the user as a `chat_syntax_leak`.
 *
 * This module is pure (no AST mutation, no `unist-util-visit`) so it
 * can be unit-tested under mocha+ts-node without ESM-only dependencies,
 * matching the existing `*-markers.ts` convention.
 */

/**
 * One attribute parsed off a tag. Values are decoded from their quoted
 * form — outer quotes (straight or smart) are removed; the value is not
 * percent-decoded (no `decodeURIComponent`) because tag attributes are
 * raw text, not URI components.
 */
export interface ParsedAttribute {
  name: string;
  value: string;
}

/** Lower-cased name + parsed attributes for a tag span. */
export interface ParsedTag {
  /** Lower-cased tag name (`badge`, `cite`, …). */
  name: string;
  /** Lower-cased attribute name → unquoted value, in source order. */
  attrs: ParsedAttribute[];
  /** Whole match (so callers can know how many input characters to consume). */
  match: string;
  /** Inner text for paired tags (`<badge>X</badge>` → `'X'`). Empty for self-closing. */
  innerText: string;
  /** `true` when the tag is self-closing or has no closing partner. */
  selfClosing: boolean;
  /** Index in the source where the match starts. */
  start: number;
  /** Index in the source where the match ends. */
  end: number;
}

/**
 * Smart / typographic quote characters that LLMs occasionally substitute
 * for straight `"` / `'` — particularly when the surrounding prose has
 * already been rendered with smart-quote autocorrect or copy-pasted from
 * a designer's spec. Treating both forms as quote delimiters means the
 * parser doesn't reject `<badge tone=“positive”>+12%</badge>` as
 * malformed.
 */
const SMART_DOUBLE_OPEN = '“';
const SMART_DOUBLE_CLOSE = '”';
const SMART_SINGLE_OPEN = '‘';
const SMART_SINGLE_CLOSE = '’';

/**
 * Attribute regex.
 *
 * Captures (in order):
 *   1: name
 *   2: double-quoted value
 *   3: single-quoted value
 *   4: smart double-quoted value (curly `“…”`)
 *   5: smart single-quoted value (curly `‘…’`)
 *   6: unquoted value (stops at whitespace, `>`, `/`, `=`)
 *
 * Implementation note: we keep this anchored to the "name = value" shape
 * — boolean attributes (no `=`) aren't a use case for `<badge>`/`<cite>`.
 * If a future tag needs them, extend this regex with an alternation and
 * add a `value: ''` fallback in the consumer.
 */
const ATTR_REGEX = new RegExp(
  '([A-Za-z_][\\w:-]*)' + // 1: name
    '\\s*=\\s*' +
    '(?:"([^"]*)"' + // 2: "..."
    "|'([^']*)'" + // 3: '...'
    `|${SMART_DOUBLE_OPEN}([^${SMART_DOUBLE_CLOSE}]*)${SMART_DOUBLE_CLOSE}` + // 4: smart "..."
    `|${SMART_SINGLE_OPEN}([^${SMART_SINGLE_CLOSE}]*)${SMART_SINGLE_CLOSE}` + // 5: smart '...'
    '|([^\\s>/=]+))', // 6: unquoted
  'g',
);

/**
 * Parse the attribute substring inside a tag (everything between the
 * tag name and the closing `>` or `/>`). Returns attributes in source
 * order with lower-cased names. Unknown shapes are silently skipped —
 * the consumer's lookup is by name so unknown junk is harmless.
 */
export function parseTagAttributes(attrSource: string): ParsedAttribute[] {
  const out: ParsedAttribute[] = [];
  // Fresh regex per call — `ATTR_REGEX` is global, so reusing it would
  // carry `lastIndex` state across calls. Same idiom as the existing
  // `*-markers.ts` modules.
  const re = new RegExp(ATTR_REGEX.source, ATTR_REGEX.flags);
  let m: RegExpExecArray | null;
  while ((m = re.exec(attrSource)) !== null) {
    const name = m[1].toLowerCase();
    // RegExpExecArray is typed as `string` per index, but optional
    // capture groups are `undefined` at runtime when unmatched. Re-type
    // locally so the chain isn't flagged as a no-op by
    // `no-unnecessary-condition`. Same idiom as the existing marker
    // helpers.
    const groups = m as unknown as Array<string | undefined>;
    const value = groups[2] ?? groups[3] ?? groups[4] ?? groups[5] ?? groups[6] ?? '';
    out.push({ name, value });
  }
  return out;
}

/** Look up an attribute by lower-cased name (case-insensitive on the input). */
export function getAttr(attrs: readonly ParsedAttribute[], name: string): string | undefined {
  const lower = name.toLowerCase();
  for (const a of attrs) {
    if (a.name === lower) return a.value;
  }
  return undefined;
}

/**
 * Build a regex that matches a single tag occurrence — paired
 * (`<name attrs>...</name>`) or self-closing (`<name attrs/>`).
 *
 * Tag names are matched case-insensitively and the closing tag's name
 * may differ in casing from the opener. The inner-text capture is
 * non-greedy so multiple back-to-back tags don't get swallowed into
 * one match.
 *
 * The resulting regex is global (`g` flag) so callers can iterate.
 *
 * Capture groups:
 *   1: opening attrs source (before `>` or `/>`)
 *   2: inner text (paired form only — `undefined` for self-closing)
 *   3: closing tag name (paired form only)
 *
 * Self-closing detection is via the trailing `/` before `>` in group 1;
 * see `findTagsInText` below.
 */
function makeTagRegex(name: string): RegExp {
  // Escape the name for safety even though our tag set ('badge','cite')
  // is alphanumeric — futureproof for tag names with hyphens etc.
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Two alternations: paired form, then self-closing form. We can't fold
  // into one because the paired form needs to capture inner text and
  // closing tag separately.
  const pattern =
    `<${escaped}\\b([^>]*?)(?:` +
    // Paired: `>inner</name>`
    `>([\\s\\S]*?)</(${escaped})\\s*>` +
    `|` +
    // Self-closing: `/>`
    `/>` +
    `)`;
  return new RegExp(pattern, 'gi');
}

/**
 * Scan a text string for non-overlapping occurrences of `<name …>…</name>`
 * or `<name … />`, returning each as a `ParsedTag`. Tag names are matched
 * case-insensitively.
 *
 * Returns the empty array when nothing matches.
 */
export function findTagsInText(text: string, name: string): ParsedTag[] {
  // Cheap pre-check before constructing the heavier regex.
  const lowerOpen = `<${name.toLowerCase()}`;
  if (!text.toLowerCase().includes(lowerOpen)) return [];

  const re = makeTagRegex(name);
  const out: ParsedTag[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    // Distinguish self-closing from paired form by whether the inner-text
    // capture group is present. We rely on regex alternation order: when
    // the paired branch matched, group 2 is a string (possibly empty);
    // when the self-closing branch matched, group 2 is undefined.
    const groups = m as unknown as Array<string | undefined>;
    const attrSource = (groups[1] ?? '').trim();
    const innerText = groups[2];
    const selfClosing = innerText === undefined;
    out.push({
      name: name.toLowerCase(),
      attrs: parseTagAttributes(attrSource),
      match: m[0],
      innerText: innerText ?? '',
      selfClosing,
      start: m.index,
      end: m.index + m[0].length,
    });
  }
  return out;
}
